<template>
  <Teleport to="body">
    <div
      class="chat-viewer fixed inset-0 z-[5000] flex select-none flex-col bg-[#101214] text-white"
      role="dialog"
      aria-modal="true"
      :aria-label="currentName"
    >
      <!-- Шапка: имя файла, счётчик, действия -->
      <div class="flex shrink-0 items-center gap-1 px-3 py-2 md:gap-2 md:px-4 md:py-3">
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium !text-white">{{ currentName }}</div>
          <div class="text-xs !text-white/60">
            <template v-if="hasMany">{{ index + 1 }} из {{ files.length }}</template>
            <template v-if="hasMany && currentSize"> · </template>
            {{ currentSize }}
          </div>
        </div>

        <template v-if="isImage">
          <button
            type="button"
            class="chat-viewer__btn flex max-md:hidden"
            title="Уменьшить (−)"
            :disabled="scale <= MIN_SCALE"
            @click="zoomBy(1 / ZOOM_STEP)"
          >
            <Icon name="lucide:zoom-out" size="20" class="block !text-white" />
          </button>
          <button
            type="button"
            class="chat-viewer__btn flex w-auto min-w-[72px] px-3 text-sm tabular-nums max-md:hidden"
            title="Реальный размер (0)"
            @click="scale === 1 ? zoomTo(naturalScale) : resetZoom()"
          >
            {{ percent }}%
          </button>
          <button
            type="button"
            class="chat-viewer__btn flex max-md:hidden"
            title="Увеличить (+)"
            :disabled="scale >= MAX_SCALE"
            @click="zoomBy(ZOOM_STEP)"
          >
            <Icon name="lucide:zoom-in" size="20" class="block !text-white" />
          </button>
        </template>

        <button type="button" class="chat-viewer__btn flex" title="Закрыть (Esc)" @click="emit('close')">
          <Icon name="lucide:x" size="22" class="block !text-white" />
        </button>
      </div>

      <!-- Сцена: клик по пустому месту закрывает, колесо и жесты — масштаб -->
      <div
        ref="stage"
        class="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden touch-none"
        @click="onStageClick"
        @wheel.prevent="onWheel"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <img
          v-if="isImage"
          ref="image"
          :key="current.url"
          :src="current.url"
          :alt="currentName"
          draggable="false"
          class="max-h-full max-w-full object-contain will-change-transform"
          :class="scale > 1 ? 'cursor-grab' : 'cursor-zoom-in'"
          :style="{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: isDragging ? 'none' : 'transform 120ms ease-out',
          }"
          @load="onImageLoad"
          @dblclick="onDoubleClick"
        />
        <video
          v-else
          :key="current.url"
          :src="current.url"
          controls
          autoplay
          playsinline
          class="max-h-full max-w-full outline-none"
        />

        <template v-if="hasMany">
          <button
            type="button"
            class="chat-viewer__nav left-2 md:left-4"
            title="Предыдущее (←)"
            @click="go(-1)"
          >
            <Icon name="lucide:chevron-left" size="28" class="block !text-white" />
          </button>
          <button
            type="button"
            class="chat-viewer__nav right-2 md:right-4"
            title="Следующее (→)"
            @click="go(1)"
          >
            <Icon name="lucide:chevron-right" size="28" class="block !text-white" />
          </button>
        </template>
      </div>

      <!-- Лента миниатюр -->
      <div
        v-if="hasMany"
        class="flex shrink-0 justify-center gap-2 overflow-x-auto px-3 py-2 md:py-3"
      >
        <button
          v-for="(file, fileIndex) in files"
          :key="`${file.url}-${fileIndex}`"
          type="button"
          class="relative size-12 shrink-0 overflow-hidden rounded-lg bg-white/10 ring-2 transition-opacity md:size-14"
          :class="fileIndex === index ? 'ring-white' : 'ring-transparent opacity-60 hover:opacity-100'"
          :title="chatFileName(file)"
          @click="select(fileIndex)"
        >
          <img
            v-if="getChatFileKind(file) === 'image'"
            :src="file.url"
            alt=""
            loading="lazy"
            class="size-full object-cover"
          />
          <span v-else class="flex size-full items-center justify-center">
            <Icon name="lucide:play" size="20" class="block !text-white" />
          </span>
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
  import type { IChatFile } from "~/interfaces/chat";
  import { chatFileName, chatFileSize, getChatFileKind } from "~/common/chatFiles";

  const props = defineProps<{
    // Только картинки и видео — документы в просмотрщик не попадают
    files: IChatFile[];
    startIndex: number;
  }>();

  const emit = defineEmits<{ (e: "close"): void }>();

  // scale = 1 — картинка вписана в сцену; наверх — до восьмикратного увеличения
  const MIN_SCALE = 1;
  const MAX_SCALE = 8;
  const ZOOM_STEP = 1.25;
  // Короткое горизонтальное движение без масштаба — перелистывание
  const SWIPE_DISTANCE = 60;

  const index = ref(props.startIndex);
  const stage = ref<HTMLElement | null>(null);
  const image = ref<HTMLImageElement | null>(null);

  const scale = ref(1);
  const translate = ref({ x: 0, y: 0 });
  // Во сколько раз реальный размер картинки больше вписанного
  const naturalScale = ref(1);
  const isDragging = ref(false);

  const files = computed(() => props.files);
  const current = computed(() => files.value[index.value]);
  const hasMany = computed(() => files.value.length > 1);
  const isImage = computed(() => getChatFileKind(current.value) === "image");
  const currentName = computed(() => chatFileName(current.value));
  const currentSize = computed(() => chatFileSize(current.value?.size));
  const percent = computed(() => Math.round((scale.value / naturalScale.value) * 100));

  const resetZoom = () => {
    scale.value = 1;
    translate.value = { x: 0, y: 0 };
  };

  const onImageLoad = () => {
    const img = image.value;
    if (!img || !img.clientWidth) return;
    naturalScale.value = Math.max(1, img.naturalWidth / img.clientWidth);
  };

  // Сдвиг не выпускает картинку за край сцены, когда она больше сцены,
  // и держит по центру, когда меньше
  const clampTranslate = () => {
    const img = image.value;
    const box = stage.value;
    if (!img || !box) return;
    const maxX = Math.max(0, (img.clientWidth * scale.value - box.clientWidth) / 2);
    const maxY = Math.max(0, (img.clientHeight * scale.value - box.clientHeight) / 2);
    translate.value = {
      x: Math.min(maxX, Math.max(-maxX, translate.value.x)),
      y: Math.min(maxY, Math.max(-maxY, translate.value.y)),
    };
  };

  // Масштабирует так, чтобы точка под курсором осталась на месте.
  // Точка задаётся относительно центра сцены.
  const zoomTo = (next: number, origin = { x: 0, y: 0 }) => {
    const target = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
    const ratio = target / scale.value;
    translate.value = {
      x: origin.x - (origin.x - translate.value.x) * ratio,
      y: origin.y - (origin.y - translate.value.y) * ratio,
    };
    scale.value = target;
    if (target === MIN_SCALE) translate.value = { x: 0, y: 0 };
    else clampTranslate();
  };

  const zoomBy = (factor: number, origin?: { x: number; y: number }) =>
    zoomTo(scale.value * factor, origin);

  const originFromEvent = (event: { clientX: number; clientY: number }) => {
    const rect = stage.value?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
  };

  const onWheel = (event: WheelEvent) => {
    if (!isImage.value) return;
    zoomBy(event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP, originFromEvent(event));
  };

  const onDoubleClick = (event: MouseEvent) => {
    if (scale.value > 1) resetZoom();
    else zoomTo(Math.max(naturalScale.value, 2), originFromEvent(event));
  };

  // Жесты: одним пальцем/мышью — сдвиг (или свайп между файлами),
  // двумя пальцами — щипок
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchDistance = 0;
  let dragStart = { x: 0, y: 0 };
  let dragTranslate = { x: 0, y: 0 };
  // Отпустили после сдвига или свайпа — это не клик по фону
  let didMove = false;

  const distance = () => {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const onStageClick = (event: MouseEvent) => {
    if (event.target === stage.value && !didMove) emit("close");
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!isImage.value) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    // Захват на самом элементе, а не на сцене: иначе клик после
    // перетаскивания картинки уходит в сцену и закрывает просмотрщик
    (event.target as Element).setPointerCapture(event.pointerId);
    didMove = false;
    if (pointers.size === 1) {
      dragStart = { x: event.clientX, y: event.clientY };
      dragTranslate = { ...translate.value };
      isDragging.value = true;
    } else if (pointers.size === 2) {
      pinchDistance = distance();
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size === 2) {
      const next = distance();
      if (pinchDistance) {
        const [a, b] = [...pointers.values()];
        zoomBy(next / pinchDistance, originFromEvent({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }));
      }
      pinchDistance = next;
      return;
    }

    if (scale.value > 1) {
      translate.value = {
        x: dragTranslate.x + event.clientX - dragStart.x,
        y: dragTranslate.y + event.clientY - dragStart.y,
      };
      clampTranslate();
    }
  };

  const onPointerUp = (event: PointerEvent) => {
    if (!pointers.has(event.pointerId)) return;
    pointers.delete(event.pointerId);
    if (pointers.size) return;

    isDragging.value = false;
    pinchDistance = 0;
    const dx = event.clientX - dragStart.x;
    const dy = event.clientY - dragStart.y;
    didMove = Math.hypot(dx, dy) > 8;
    if (scale.value === 1 && hasMany.value && Math.abs(dx) > SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy)) {
      go(dx < 0 ? 1 : -1);
    }
  };

  const select = (next: number) => {
    index.value = (next + files.value.length) % files.value.length;
    resetZoom();
  };
  const go = (delta: number) => select(index.value + delta);

  const onKeydown = (event: KeyboardEvent) => {
    switch (event.key) {
      case "Escape":
        emit("close");
        break;
      case "ArrowLeft":
        if (hasMany.value) go(-1);
        break;
      case "ArrowRight":
        if (hasMany.value) go(1);
        break;
      case "+":
      case "=":
        if (isImage.value) zoomBy(ZOOM_STEP);
        break;
      case "-":
        if (isImage.value) zoomBy(1 / ZOOM_STEP);
        break;
      case "0":
        if (isImage.value) resetZoom();
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  // Страница под просмотрщиком не должна прокручиваться колесом и свайпами
  let bodyOverflow = "";

  onMounted(() => {
    window.addEventListener("keydown", onKeydown);
    window.addEventListener("resize", clampTranslate);
    bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKeydown);
    window.removeEventListener("resize", clampTranslate);
    document.body.style.overflow = bodyOverflow;
  });
</script>

<style lang="scss" scoped>
  .chat-viewer__btn {
    @apply size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/10;
  }

  .chat-viewer__nav {
    @apply absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 md:size-12;
  }
</style>
